import cx from 'classnames';
import type { ReactNode } from 'react';

import { MarkdownText } from './MarkdownText';
import type { ChatMessage } from './types';

interface AssistantMessageProps {
  message: ChatMessage;
  /**
   * Optional slot rendered below the assistant bubble after streaming
   * completes — typically a `MessageFeedback` thumbs row. The slot is
   * omitted while `message.isStreaming` is true so the controls don't
   * flicker into view mid-turn.
   */
  feedbackSlot?: ReactNode;
  className?: string;
}

/**
 * Left-aligned assistant bubble. Markdown rendering happens in
 * MarkdownText. A streaming cursor stays parked to the right of the text
 * while `message.isStreaming` is true; the cursor's footprint is reserved
 * so the bubble doesn't re-flow when streaming completes.
 */
export function AssistantMessage({
  message,
  feedbackSlot,
  className,
}: AssistantMessageProps) {
  return (
    <div className={cx('tw-flex', 'tw-justify-start', className)}>
      <div
        className={cx(
          'tw-max-w-[90%]',
          'tw-text-14',
          'tw-text-label-secondary',
          'tw-leading-[1.6]',
          'tw-break-words',
        )}
      >
        <MarkdownText text={message.text} />
        {message.isStreaming && (
          // Inline 2px-wide cursor that pulses while the assistant turn
          // is streaming. Rendered as inline-block so it can sit beside
          // the last word of the markdown output (MarkdownText's tail
          // paragraph). aria-hidden — the live-region announcement
          // lives in MessageList, so the cursor stays purely visual.
          <span
            aria-hidden="true"
            className={cx(
              'tw-inline-block',
              'tw-w-[2px]',
              'tw-h-[18px]',
              'tw-ml-2',
              'tw-align-middle',
              'tw-bg-label-primary',
              'tw-animate-pulse',
            )}
          />
        )}
        {!message.isStreaming && feedbackSlot}
      </div>
    </div>
  );
}
